import React from 'react'
import { useNavigation } from '@react-navigation/native'
import type { NavigationProp } from '@react-navigation/native'
import { camelizeKeys } from 'humps'

import { usePostContext, useChatContext, ChatPostProps } from 'APP/Lib/Context'
import { channelToTimeStampForNewPost } from 'APP/Lib/MessagingHelpers'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import PatientHistoryActions from 'APP/Redux/PatientHistoryRedux'
import Analytics from 'APP/Services/Analytics'
import { PromptCheckoutProps } from 'APP/Services/Mattermost/types'
import { useCreateCharge } from 'APP/Hooks/useCreateCharge'

import Card from 'APP/Components/Chat/Card'
import { CHECKOUT_TYPE, CheckoutContext, CheckoutProps, FeePolicy } from 'APP/Components/Checkout'
import { handleCheckoutConfirm } from 'APP/Components/Checkout/helpers'

type SendAnswerFn = (reply: { payload: unknown; display: string }) => void

const Checkout = () => {
  const { channelId, userId, conversation } = useChatContext()
  const { post, isLastPost } = usePostContext()
  const navigation = useNavigation<NavigationProp<ReactNavigation.RootParamList>>()
  const dispatch = useDispatch()
  const episodeId = useSelector((state) => state.history.currentChannelId)

  const { createCharge } = useCreateCharge()

  if (!post || !channelId || !userId || !episodeId) return null

  const title = post.message!
  const checkoutPostProps = post.props! as ChatPostProps & PromptCheckoutProps

  const subtitle = checkoutPostProps.description!
  const checkoutType = checkoutPostProps.checkout_type as CHECKOUT_TYPE
  const feePolicy = camelizeKeys(checkoutPostProps.fee_policy) as FeePolicy
  const checkoutContext = camelizeKeys(
    checkoutPostProps.context
  ) as unknown as CheckoutContext<CHECKOUT_TYPE>

  const sendAnswer: SendAnswerFn = ({ display, payload }) => {
    dispatch(
      PatientHistoryActions.submitAnswerPostRequest(
        channelId,
        userId,
        {
          text: display,
          data: [payload],
          question_type: 'multiChoiceQuestion',
        },
        channelToTimeStampForNewPost(conversation),
        { update_channel_state: false }
      )
    )
    dispatch(PatientHistoryActions.updateChannelLastViewed(channelId!))
  }

  const onConfirm = async (ctx: CheckoutContext<CHECKOUT_TYPE>) => {
    await handleCheckoutConfirm({
      feePolicy,
      episodeId,
      checkoutType,
      checkoutContext: ctx,
      createCharge,
      sendAnswer,
    })
  }

  const onPress = () => {
    const checkoutProps = {
      feePolicy,
      context: checkoutContext,
      type: checkoutType,
      onConfirm,
    } as CheckoutProps

    navigation.navigate('checkoutScreen', {
      title,
      subtitle,
      ...checkoutProps,
    })

    Analytics.trackEvent('button_click', {
      button_value: `Open Checkout Screen`,
    })
  }

  return <Card title={title} onPress={isLastPost ? onPress : undefined} testID="CHECKOUT_CARD" />
}

export default Checkout
